import React, { useEffect, useRef } from 'react';
import { CreditCard } from 'lucide-react';

const CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
const NS = 'PayPalSDK'; // eigener Namespace, damit der Checkout-PayPal-SDK nicht kollidiert
let sdkPromise = null;

function loadMessagesSdk() {
  if (!CLIENT_ID) return Promise.reject(new Error('no paypal client id'));
  if (window[NS] && window[NS].Messages) return Promise.resolve();
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(CLIENT_ID)}&components=messages&currency=EUR`;
    s.async = true;
    s.setAttribute('data-namespace', NS);
    s.onload = () => resolve();
    s.onerror = () => { sdkPromise = null; reject(new Error('paypal sdk load failed')); };
    document.head.appendChild(s);
  });
  return sdkPromise;
}

function fmtEUR(n) {
  return (Number(n) || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

/**
 * v227 Masterplan: PayPal Ratenzahlung viel praesenter.
 *
 * Zeigt eine grosse Finanzierungs-Box mit dynamisch berechneter Monatsrate:
 *   - >= 1000 EUR: "Ab X EUR/Monat mit PayPal Ratenzahlung (24 Raten)"
 *   - 300 - 999:   "Oder 4 x X EUR in 4 zinsfreien Raten"
 *   - < 300:       PayPal On-Site Messaging (Fallback via SDK)
 *
 * placement: 'product' | 'cart' | 'payment' | 'home'
 * boxed: rendert eine auffaellige Finanzierungs-Box (CRO) statt nur einer Textzeile.
 */
export default function PayPalMessage({ amount, placement = 'product', textColor = 'white',
                                       logoType = 'primary', className = '', boxed = false }) {
  const ref = useRef(null);
  const amt = Number(amount) || 0;
  const rate24 = amt >= 1000 ? amt / 24 : 0;
  const rate4  = amt >= 300 && amt < 1000 ? amt / 4 : 0;

  useEffect(() => {
    let cancelled = false;
    let inner = null;
    if (!CLIENT_ID || !amt || amt <= 0) return;
    // v242a: PayPal-SDK darf NIEMALS direkt in ein React-verwaltetes Element rendern.
    // React versucht sonst spaeter dessen Children zu unmounten und crasht mit
    // "removeChild"/"insertBefore" wenn PayPal's DOM-Manipulationen dazwischen liefen.
    // Loesung: eigenen Container erzeugen, React sieht nur den leeren Wrapper.
    loadMessagesSdk().then(() => {
      const sdk = window[NS];
      if (cancelled || !ref.current || !(sdk && sdk.Messages)) return;
      // Alten Container (falls Re-Render) sicher entfernen
      while (ref.current.firstChild) {
        try { ref.current.removeChild(ref.current.firstChild); } catch (e) { break; }
      }
      inner = document.createElement('div');
      inner.setAttribute('data-paypal-render', '1');
      ref.current.appendChild(inner);
      try {
        sdk.Messages({
          amount: amt,
          placement,
          style: boxed
            ? { layout: 'text', logo: { type: 'primary', position: 'left' }, text: { color: 'black', size: 14 } }
            : { layout: 'text', logo: { type: logoType }, text: { color: textColor, size: 14 } },
        }).render(inner);
      } catch (e) { /* silent */ }
    }).catch(() => {});
    return () => {
      cancelled = true;
      // Container synchron entfernen bevor React den Wrapper unmountet.
      if (inner && inner.parentNode) {
        try { inner.parentNode.removeChild(inner); } catch (e) { /* silent */ }
      }
    };
  }, [amount, placement, textColor, logoType, boxed, amt]);

  if (!CLIENT_ID) return null;

  // Neue prominente Box mit unserer eigenen Monatsrate + PayPal SDK darunter
  if (boxed) {
    return (
      <div className={`pp-finance-box pp-finance-boxed ${className}`} data-testid="paypal-finance-box" style={{
        background: 'linear-gradient(135deg, rgba(0,48,135,0.08) 0%, rgba(0,156,222,0.08) 100%)',
        border: '1px solid rgba(0,156,222,0.4)',
        borderRadius: 10,
        padding: '12px 14px',
        margin: '10px 0',
      }}>
        <div className="pp-finance-head" style={{
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: rate24 > 0 || rate4 > 0 ? 6 : 4,
        }}>
          <span className="pp-finance-icon" style={{
            background: '#003087', color: 'white', width: 24, height: 24, borderRadius: 5,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <CreditCard size={14} />
          </span>
          <span className="pp-finance-title" style={{ fontWeight: 700, fontSize: 13, color: '#f0f0f0', letterSpacing: 0.2 }}>
            <span style={{ color: '#009cde' }}>Pay</span><span style={{ color: '#003087' }}>Pal</span> Ratenzahlung – jetzt kaufen, später bezahlen
          </span>
        </div>

        {rate24 > 0 && (
          <div data-testid="paypal-rate-24" style={{
            color: '#4dd0ff', fontSize: 15, fontWeight: 700, lineHeight: 1.25, marginBottom: 4,
          }}>
            Ab {fmtEUR(rate24)}/Monat <span style={{ opacity: 0.75, fontSize: 12, fontWeight: 500 }}>· 24 Raten, bonitätsabhängig</span>
          </div>
        )}
        {rate4 > 0 && (
          <div data-testid="paypal-rate-4" style={{
            color: '#4dd0ff', fontSize: 15, fontWeight: 700, lineHeight: 1.25, marginBottom: 4,
          }}>
            Oder 4 × {fmtEUR(rate4)} <span style={{ opacity: 0.75, fontSize: 12, fontWeight: 500 }}>· zinsfrei in 4 Raten alle 14 Tage</span>
          </div>
        )}
        {(rate24 === 0 && rate4 === 0) && (
          <div style={{ color: '#cfe9ff', fontSize: 13 }}>
            Rechnungskauf oder in <strong>Raten</strong> zahlen – ohne Zusatzkosten für die üblichen Optionen.
          </div>
        )}

        <div ref={ref} className="paypal-message pp-finance-msg" data-testid="paypal-message" style={{
          marginTop: 4, minHeight: 18, fontSize: 12, opacity: 0.9,
        }} />
      </div>
    );
  }

  // Kompakter Inline-Modus (unverändert für z.B. Home)
  return <div ref={ref} className={`paypal-message ${className}`} data-testid="paypal-message" />;
}
